import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { navigate, type Route } from "../router";
import { resolve, type KeyContext } from "../shortcuts";
import { useSession } from "../state/session";
import { useStatus } from "../state/status";
import { getTheme, toggleTheme, type Theme } from "../theme/theme";
import { ChangePasswordDialog } from "../dialogs/ChangePasswordDialog";
import { useAuthConfig } from "../state/useAuthConfig";
import { AdminDialog } from "../dialogs/AdminDialog";
import { ClassManagerDialog } from "../dialogs/ClassManagerDialog";
import { ExportDialog } from "../dialogs/ExportDialog";
import { ProjectSetupDialog } from "../dialogs/ProjectSetupDialog";
import { MeasurementsDialog, clearUnreviewedAnnotations, resetReviewState } from "../dialogs/projectTools";
import {
  PROJECTS_CHANGED, PROJECT_DATA_CHANGED, getCurrentProjectId, projectDataChanged, projectsChanged, setCurrentProjectId, useCurrentProjectId,
} from "../state/currentProject";
import { useDialogs } from "./dialogs";
import { ShortcutsDialog } from "../dialogs/ShortcutsDialog";
import { IMPLEMENTED } from "../menus";
import { TopBar } from "./TopBar";
import { SideNav, type NavId } from "./SideNav";
import { UpdateBanner } from "./UpdateBanner";
import { Icon } from "./icons";
import { credits as readCredits } from "../api/auth";
import { annotationStats, type AnnotationStats } from "../api/ml";
import { createProject, listProjects } from "../api/projects";
import type { CreditStatus, Project } from "../api/types";
import { CREDITS_CHANGED } from "../lib/credits";
import { ShellCtx, type ActionCtx, type ActionMap, type ShellApi } from "./shellActions";
import type { MenuState } from "./ActionMenu";

/** Shortcut ids the shell acts on today, and the action each runs. */
const SHORTCUT_ACTIONS: Record<string, string> = {
  "app.theme": "theme.toggle", "help.shortcuts": "help.shortcuts", "app.dashboard": "nav.dashboard",
};

/** The project a route is about, if any. */
const routeProject = (r: Route): number | null => ("projectId" in r ? r.projectId : null);

type ShellDialog = null | "password" | "shortcuts" | "admin" | "mx-csv" | "mx-xlsx" | "classes" | "export";

/** The app frame: a top bar, the project's side navigation, the page, and toasts. */
export function Shell({ route, children }: { route: Route; children: ReactNode }) {
  const { user, logout, isAdmin, canEdit, has } = useSession();
  const { toasts, say, dismiss } = useStatus();
  const [theme, setTheme] = useState<Theme>(getTheme());
  const [dialog, setDialog] = useState<ShellDialog>(null);
  const [toolProject, setToolProject] = useState(0);
  // A project just made from the switcher, whose setup an admin is asked for at once.
  const [setupFor, setSetupFor] = useState<Project | null>(null);
  const dlg = useDialogs();
  const authConfig = useAuthConfig();

  // The project in context: the route's, or the one selected on the dashboard. A page about a project selects it, so
  // the dashboard finds it selected on the way back.
  const storeProject = useCurrentProjectId();
  const projectId = routeProject(route) ?? (route.name === "dashboard" ? storeProject : null);
  useEffect(() => { const id = routeProject(route); if (id !== null) setCurrentProjectId(id); }, [route]);

  // The organization's projects, for the switcher.
  const [projects, setProjects] = useState<Project[]>([]);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = () => { listProjects().then((ps) => alive && setProjects(ps)).catch(() => {}); };
    load();
    window.addEventListener(PROJECTS_CHANGED, load);
    return () => { alive = false; window.removeEventListener(PROJECTS_CHANGED, load); };
  }, [user]);
  const project = projects.find((p) => p.id === projectId) ?? null;

  // The project's counts for the side nav: on entering a page, when a page reports a change, and every minute.
  const [stats, setStats] = useState<AnnotationStats | null>(null);
  useEffect(() => {
    if (projectId === null) { setStats(null); return; }
    let alive = true;
    const load = () => { annotationStats(projectId).then((s) => alive && setStats(s)).catch(() => alive && setStats(null)); };
    load();
    const timer = window.setInterval(load, 60_000);
    window.addEventListener(PROJECT_DATA_CHANGED, load);
    return () => { alive = false; window.clearInterval(timer); window.removeEventListener(PROJECT_DATA_CHANGED, load); };
  }, [projectId, route.name]);

  // The organization's credits: read on sign-in, every minute, and at once when a page spends some.
  const [credits, setCredits] = useState<CreditStatus | null>(null);
  useEffect(() => {
    if (!user) { setCredits(null); return; }
    let alive = true;
    const load = () => { readCredits().then((c) => alive && setCredits(c)).catch(() => {}); };
    load();
    const timer = window.setInterval(load, 60_000);
    window.addEventListener(CREDITS_CHANGED, load);
    return () => { alive = false; window.clearInterval(timer); window.removeEventListener(CREDITS_CHANGED, load); };
  }, [user]);

  // Pages register the actions they implement; those win over the shell's own.
  const [registered, setRegistered] = useState<ActionMap>({});
  const regRef = useRef<ActionMap>({});
  regRef.current = registered;
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const probeRef = useRef<(() => Partial<KeyContext>) | undefined>(undefined);

  /**
   * The project menu's Reset review state… and the rest act on the project you have open: the annotation page's, or the
   * one selected on the dashboard. The page saves first and reloads afterwards.
   */
  const projectTool = useCallback(async (action: string) => {
    const pid = route.name === "annotate" ? route.projectId : route.name === "dashboard" ? getCurrentProjectId() : null;
    const title = { "review.reset": "Reset review state", "review.clear": "Clear unreviewed annotations", "export.measurements": "Export measurements", "export.measurements.xlsx": "Export measurements to Excel" }[action]!;
    if (pid === null) { await dlg.alert({ title, text: "Open or select a project first — this acts on the project you have open." }); return; }
    await regRef.current["page.before"]?.();
    if (action === "export.measurements" || action === "export.measurements.xlsx") { setToolProject(pid); setDialog(action === "export.measurements" ? "mx-csv" : "mx-xlsx"); return; }
    const changed = await (action === "review.reset" ? resetReviewState : clearUnreviewedAnnotations)(pid, dlg, say);
    if (changed) { await regRef.current["page.after"]?.(); projectDataChanged(); }
  }, [route, dlg, say]);

  const run = useCallback((action: string, ctx?: ActionCtx) => {
    const extra = regRef.current[action];
    if (extra) return extra(ctx);
    switch (action) {
      case "review.reset": case "review.clear": case "export.measurements": case "export.measurements.xlsx": void projectTool(action); break;
      case "nav.dashboard": navigate({ name: "dashboard" }); break;
      case "logout": logout(); break;
      case "password.change": setDialog("password"); break;
      case "help.shortcuts": setDialog("shortcuts"); break;
      // A server with account services has the full administration page; the desktop keeps its dialog.
      case "admin.open": if (authConfig.account_services_enabled) navigate({ name: "admin" }); else setDialog("admin"); break;
      case "theme.toggle": setTheme(toggleTheme()); break;
    }
  }, [logout, projectTool, authConfig.account_services_enabled]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const hit = resolve(e, {
        annotateView: route.name === "annotate",
        canvasFocused: !!t?.closest?.("[data-canvas]"),
        textFieldFocused: !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)),
        mac: /Mac/.test(navigator.platform),
        ...probeRef.current?.(),
      });
      if (!hit) return;
      const action = regRef.current[SHORTCUT_ACTIONS[hit.id] ?? hit.id] ? (SHORTCUT_ACTIONS[hit.id] ?? hit.id) : SHORTCUT_ACTIONS[hit.id];
      if (!action) return;
      e.preventDefault();                       // the app handled it: the browser must not also act on it
      run(action, { arg: hit.arg, event: e });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [route.name, run]);

  const api = useMemo<ShellApi>(() => ({
    register(actions, probe) {
      setRegistered(actions);
      probeRef.current = probe;
      return () => { setRegistered((cur) => (cur === actions ? {} : cur)); setChecked({}); if (probeRef.current === probe) probeRef.current = undefined; };
    },
    setChecked,
    run,
  }), [run]);

  // The update banner's Refresh: let the page save first (the annotation page's pending edits), then reload.
  const refreshApp = useCallback(async () => {
    try { await regRef.current["page.before"]?.(); } catch { /* reload anyway: the page has already reported it */ }
    window.location.reload();
  }, []);

  /** Switching project keeps you on the same kind of page. */
  const switchProject = (p: Project | null) => {
    setCurrentProjectId(p?.id ?? null);
    if (p && (route.name === "annotate" || route.name === "augment" || route.name === "train" || route.name === "models" || route.name === "auto")) navigate({ name: route.name, projectId: p.id });
    else if (route.name !== "dashboard") navigate({ name: "dashboard" });
  };

  /** The switcher's New project…: create it, then open it on the dashboard. */
  const newProject = async () => {
    const name = (await dlg.prompt({ title: "New project", label: "Project name" }))?.trim();
    if (!name) return;
    try {
      const p = await createProject(name);
      setProjects((ps) => [...ps, p]);
      projectsChanged();
      setCurrentProjectId(p.id);
      if (route.name !== "dashboard") navigate({ name: "dashboard" });
      if (isAdmin) setSetupFor(p);
    } catch (e) { await dlg.alert({ title: "Could not create the project", text: (e as Error).message }); }
  };

  const go = async (id: NavId) => {
    if (id === "admin") { run("admin.open"); return; }
    if (projectId === null) return;
    if (id === "images") { setCurrentProjectId(projectId); navigate({ name: "dashboard" }); return; }
    if (id === "annotate" || id === "augment" || id === "train" || id === "models" || id === "auto") { navigate({ name: id, projectId }); return; }
    await regRef.current["page.before"]?.();     // the annotation page saves before a dialog that changes classes
    setToolProject(projectId);
    setDialog(id);
  };

  const active: NavId | null = route.name === "dashboard" ? "images" : route.name === "annotate" ? "annotate" : route.name === "train" ? "train"
    : route.name === "augment" ? "augment" : route.name === "models" ? "models" : route.name === "auto" ? "auto" : route.name === "admin" ? "admin" : dialog === "classes" ? "classes" : null;
  const implemented = new Set([...IMPLEMENTED, ...Object.keys(registered)]);
  const menuState: MenuState = { admin: isAdmin, canEdit, annotateView: route.name === "annotate", hasProject: projectId !== null, implemented, checked, has };
  const inAdmin = route.name === "admin";

  return (
    <div className="app">
      <TopBar user={user} theme={theme} credits={credits} projects={projects} project={project} showProject={!inAdmin}
        menuState={menuState} onAction={run} onSwitch={switchProject} onNew={() => void newProject()} canCreate={canEdit} />
      <UpdateBanner onRefresh={refreshApp} />
      {!canEdit && (
        <div role="note" className="readonly-banner" data-testid="readonly-banner">
          <Icon name="info" size={14} />Read-only access: your role ({user?.role}) can look at projects and results but not change them.
        </div>
      )}
      <div className="app-body">
        {!inAdmin && <SideNav project={project} stats={stats} active={active} compact={route.name === "annotate"} isAdmin={isAdmin} canEdit={canEdit} augmentOpen={has("augment_images")} onGo={go} />}
        <main className="app-main" data-testid="page"><ShellCtx.Provider value={api}>{children}</ShellCtx.Provider></main>
      </div>
      <div className="toasts" role="status" aria-live="polite" data-testid="toasts">
        {toasts.map((t) => (
          <div key={t.id} className="toast" data-testid="toast">
            <Icon name="check" size={15} /><span>{t.text}</span>
            {t.details && <button className="toast-link" data-testid="toast-details"
              onClick={() => { const d = t.details!; dismiss(t.id); void dlg.alert({ title: d.title, text: d.text }); }}>Details</button>}
            <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss"><Icon name="x" size={14} /></button>
          </div>
        ))}
      </div>
      {dialog === "password" && <ChangePasswordDialog onClose={() => setDialog(null)} onDone={() => say("Your password has been updated.")} />}
      {dialog === "shortcuts" && <ShortcutsDialog onClose={() => setDialog(null)} />}
      {dialog === "admin" && <AdminDialog onClose={() => setDialog(null)} />}
      {dialog === "classes" && <ClassManagerDialog projectId={toolProject}
        onClose={() => { setDialog(null); void regRef.current["page.after"]?.(); projectDataChanged(); }} />}
      {dialog === "export" && <ExportDialog projectId={toolProject} onClose={() => setDialog(null)} />}
      {setupFor && <ProjectSetupDialog project={setupFor} hasModels={false} has={has} onClose={() => setSetupFor(null)}
        onSaved={(p) => { setProjects((ps) => ps.map((x) => (x.id === p.id ? p : x))); setSetupFor(null); projectsChanged(); }} />}
      {(dialog === "mx-csv" || dialog === "mx-xlsx") && <MeasurementsDialog projectId={toolProject} xlsx={dialog === "mx-xlsx"} dlg={dlg} say={say} onClose={() => setDialog(null)} />}
    </div>
  );
}
